/**
 * 表格数据分页切片函数
 * @param {Object} params - 参数对象
 * @param {Array} params.data - 表格数据数组
 * @param {number} params.currentPage - 当前分页数（从1开始）
 * @param {number} params.pageSize - 分页数量
 * @returns {Array} 分页后的数据切片
 */
export const paginateTableData = ({ data, currentPage, pageSize }) => {
  if (!Array.isArray(data)) return []

  if (typeof currentPage !== 'number' || currentPage < 1) return []

  if (typeof pageSize !== 'number' || pageSize < 1) return []

  // 计算起始索引和结束索引
  const startIndex = (currentPage - 1) * pageSize
  const endIndex = startIndex + pageSize

  // 如果起始索引超出数组范围，返回空数组
  if (startIndex >= data.length) return []

  // 返回分页切片
  return data.slice(startIndex, endIndex)
}

/**
 * 多条件查询函数，支持多个字段的不同查询值
 * @param {Object} params - 参数对象
 * @param {Array} params.data - 表格数据数组
 * @param {Object} params.queries - 查询条件对象，格式为 {字段名: 查询值, ...}
 * @param {boolean} [params.caseSensitive=false] - 是否区分大小写
 * @param {boolean} [params.matchAll=true] - 是否需全部匹配（默认AND查询）
 * @returns {Array} 过滤后的数据数组
 */
export const searchTableData = ({
  data,
  queries,
  caseSensitive = false,
  matchAll = true,
}) => {
  // 参数验证
  if (!Array.isArray(data)) return []
  if (data.length === 0) return []
  if (!queries || typeof queries !== 'object') return data
  if (Object.keys(queries).length === 0) return data

  // 过滤掉空值的查询条件
  const validQueries = Object.entries(queries).reduce((acc, [field, value]) => {
    if (value != null && value !== '' && value !== undefined) {
      acc[field] = value
    }
    return acc
  }, {})

  // 如果没有有效的查询条件，返回原数据
  if (Object.keys(validQueries).length === 0) return data

  return data.filter((item) => {
    if (item == null) return false

    // 对每个查询条件进行匹配
    const conditionMatches = Object.entries(validQueries).map(
      ([field, queryValue]) => {
        // 安全属性检查
        if (!Object.prototype.hasOwnProperty.call(item, field)) {
          return false
        }

        const itemValue = item[field]
        if (itemValue == null) return false

        // 处理查询值和数据值
        const searchValue = caseSensitive
          ? String(queryValue)
          : String(queryValue).toLowerCase()

        const itemValueStr = caseSensitive
          ? String(itemValue)
          : String(itemValue).toLowerCase()

        return itemValueStr.includes(searchValue)
      }
    )

    // 根据 matchAll 决定匹配逻辑
    return matchAll
      ? conditionMatches.every((match) => match) // AND 查询：所有条件都要匹配
      : conditionMatches.some((match) => match) // OR 查询：任意条件匹配即可
  })
}

/**
 * 智能多字段排序函数（自动类型检测）
 * @param {Object} params - 参数对象
 * @param {Array} params.data - 要排序的数据数组
 * @param {Array} params.sortBy - 排序配置数组
 * @returns {Array} 排序后的数组
 */
export const sortTableData = ({ data, sortBy }) => {
  if (!Array.isArray(data)) return []

  if (!Array.isArray(sortBy) || sortBy.length === 0) return data

  if (data.length === 0) return []

  // 创建数据副本
  const sortedData = [...data]

  // 类型检测器
  const detectType = (value) => {
    if (value == null) return 'null'

    // 1. 检测数字（包括字符串形式的数字）
    if (!isNaN(Number(value)) && value !== '') {
      // 排除纯空格字符串等特殊情况
      const num = Number(value)
      if (
        typeof value === 'number' ||
        (typeof value === 'string' && String(num) === value.trim())
      ) {
        return 'number'
      }
    }

    // 2. 检测日期时间（常见格式）
    if (typeof value === 'string') {
      // 标准日期时间格式：2024-01-15 10:30:00, 2024-01-15, 2024/01/15等
      const dateTimePatterns = [
        /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/, // 2024-01-15 10:30:00
        /^\d{4}-\d{2}-\d{2}$/, // 2024-01-15
        /^\d{4}\/\d{2}\/\d{2} \d{2}:\d{2}:\d{2}$/, // 2024/01/15 10:30:00
        /^\d{4}\/\d{2}\/\d{2}$/, // 2024/01/15
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO格式
      ]

      for (const pattern of dateTimePatterns) {
        if (pattern.test(value.trim())) {
          const date = new Date(value)
          if (!isNaN(date.getTime())) {
            return 'datetime'
          }
        }
      }
    }

    // 3. 默认为字符串
    return 'string'
  }

  // 智能类型检测（基于样本数据）
  const createSmartComparator = (field) => {
    // 从数据中采样检测类型
    const samples = data
      .filter((item) => item[field] != null)
      .slice(0, 10) // 采样前10个非空值
      .map((item) => item[field])

    if (samples.length === 0) return 'string' // 默认

    // 统计类型分布
    const typeCounts = {}
    samples.forEach((sample) => {
      const type = detectType(sample)
      typeCounts[type] = (typeCounts[type] || 0) + 1
    })

    // 返回出现最多的类型
    return Object.keys(typeCounts).reduce((a, b) =>
      typeCounts[a] > typeCounts[b] ? a : b
    )
  }

  // 预计算每个字段的类型
  const fieldTypes = {} as any
  sortBy.forEach((config) => {
    fieldTypes[config.field] = createSmartComparator(config.field) // 自动检测
  })

  // 比较函数
  const compareValues = (a, b, fieldConfig) => {
    const { field, order = 'asc' } = fieldConfig
    const type = fieldTypes[field]

    const valueA = a[field]
    const valueB = b[field]

    // 处理空值
    if (valueA == null && valueB == null) return 0
    if (valueA == null) return order === 'asc' ? -1 : 1
    if (valueB == null) return order === 'asc' ? 1 : -1

    // 根据检测到的类型进行比较
    switch (type) {
      case 'number':
        return compareNumbers(valueA, valueB, order)

      case 'datetime':
        return compareDatetimes(valueA, valueB, order)

      case 'string':
      default:
        return compareStrings(valueA, valueB, order)
    }
  }

  // 数字比较
  const compareNumbers = (a, b, order) => {
    const numA = Number(a)
    const numB = Number(b)

    if (isNaN(numA) && isNaN(numB)) return 0
    if (isNaN(numA)) return order === 'asc' ? -1 : 1
    if (isNaN(numB)) return order === 'asc' ? 1 : -1

    const comparison = numA - numB
    return order === 'asc' ? comparison : -comparison
  }

  // 日期时间比较
  const compareDatetimes = (a, b, order) => {
    const dateA = new Date(a)
    const dateB = new Date(b)

    if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) {
      return compareStrings(a, b, order)
    }
    if (isNaN(dateA.getTime())) return order === 'asc' ? -1 : 1
    if (isNaN(dateB.getTime())) return order === 'asc' ? 1 : -1

    const timeA = dateA.getTime()
    const timeB = dateB.getTime()

    if (timeA < timeB) return order === 'asc' ? -1 : 1
    if (timeA > timeB) return order === 'asc' ? 1 : -1
    return 0
  }

  // 字符串比较
  const compareStrings = (a, b, order) => {
    const strA = String(a).toLowerCase()
    const strB = String(b).toLowerCase()

    if (strA < strB) return order === 'asc' ? -1 : 1
    if (strA > strB) return order === 'asc' ? 1 : -1
    return 0
  }

  // 主排序
  sortedData.sort((a, b) => {
    for (const sortConfig of sortBy) {
      const result = compareValues(a, b, sortConfig)
      if (result !== 0) return result
    }
    return 0
  })

  return sortedData
}

// 处理静态列表数据参数
export interface ProcessStaticDataParams {
  data: any[] // 数据列表

  // 查询配置
  search?: {
    queries: Record<string, any> // {字段名: 查询值}// 多字段查询的键值对
    caseSensitive?: boolean // 是否区分大小写（默认值false不区分）
    matchAll?: boolean // 多字段时是否需全部匹配（默认值true全部匹配）
    [key: string]: any
  }

  // 排序配置
  sort?: {
    field: string // 排序字段
    order?: 'asc' | 'desc' // 排序顺序
    [key: string]: any
  }[]

  // 分页配置
  paginate?: {
    current: number // 当前分页数
    pageSize?: number // 每页条数
    [key: string]: any
  }
  [key: string]: any
}

/**
 * 表格数据处理整合函数（查询 + 排序 + 分页）
 * @param {Object} params - 参数对象
 * @param {Array} params.data - 原始数据数组
 *
 * @param {Object} [params.search] - 查询配置（不传则跳过查询）
 * @param {string|string[]} [params.search.queries] - 查询键值对
 *
 * @param {Array} [params.sort] - 排序规则数组（不传则跳过排序）
 *
 * @param {Object} [params.paginate] - 分页配置（不传则跳过分页）
 * @param {number} [params.paginate.current] - 当前页码
 * @param {number} [params.paginate.pageSize] - 每页大小
 * @returns {Object} 处理结果对象
 *
 * {data:[原始数据数组], search:{查询配置}, sort:[排序配置], paginate:{分页配置}}
 */
export const processStaticData = ({
  data,
  search,
  sort,
  paginate,
}: ProcessStaticDataParams) => {
  // 参数验证
  if (!Array.isArray(data)) {
    return {
      items: [],
      total: 0,
    }
  }

  let items = [...data]
  let filteredTotal = data.length

  // 步骤1：查询过滤（只有在传入了search参数且包含有效查询值时执行）
  const shouldSearch = search && Object.keys(search.queries).length >= 0

  if (shouldSearch) {
    items = searchTableData({
      data: items,
      queries: search.queries,
      caseSensitive: search.caseSensitive,
      matchAll: search.matchAll,
    })
    filteredTotal = items.length
  }

  // 步骤2：排序（只有在传入了sort参数且包含有效的sortBy数组时执行）
  const shouldSort = sort && Array.isArray(sort) && sort.length > 0

  if (shouldSort) {
    // 确保排序配置符合 sortTableData 的期望格式
    const sortBy = sort.map((item) => ({
      order: item.order || 'asc',
      ...item,
    }))

    items = sortTableData({
      data: items,
      sortBy: sortBy,
    })
  }

  // 步骤3：分页（只有在传入了paginate参数时执行）
  const shouldPaginate = paginate != null

  if (shouldPaginate) {
    const current = Math.max(1, paginate.current || 1)
    const pageSize = Math.max(1, paginate.pageSize || 10)

    items = paginateTableData({
      data: items,
      currentPage: current,
      pageSize: pageSize,
    })
  }

  return {
    items,
    total: filteredTotal, // 过滤后的总数据量
  }
}
